import "./styles.css";
import { GameAudio } from "./audio";
import { CombatLab } from "./combat-lab";
import { FixedStepDriver } from "./fixed-step-driver";
import type { FighterId, GameEvent } from "./game-types";
import { PLACEHOLDER_TEMPLATES } from "./fighter-config";
import { KeyboardInput } from "./input";
import { GameRenderer } from "./renderer";
import {
  AVAILABLE_FIGHTERS,
  ROSTER,
  getDefaultRival,
  getRosterFighter,
  isPlayableFighter,
} from "./roster";
import { FIXED_STEP, GameSimulation, WORLD_HEIGHT } from "./simulation";

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing required element #${id}`);
  }
  return element as T;
}

const canvas = requireElement<HTMLCanvasElement>("game-canvas");
const gameShell = requireElement<HTMLElement>("game-shell");
const loadingScreen = requireElement<HTMLElement>("loading-screen");
const loadingProgress = requireElement<HTMLElement>("loading-progress");
const titleScreen = requireElement<HTMLElement>("title-screen");
const characterSelect = requireElement<HTMLElement>("character-select");
const selectShowcase = requireElement<HTMLElement>("select-showcase");
const rosterGrid = requireElement<HTMLElement>("roster-grid");
const selectedNameGhost = requireElement<HTMLElement>("selected-name-ghost");
const selectedFighterImage =
  requireElement<HTMLImageElement>("selected-fighter-image");
const selectedFighterNumber =
  requireElement<HTMLElement>("selected-fighter-number");
const selectedFighterName = requireElement<HTMLElement>("selected-fighter-name");
const selectedFighterTitle =
  requireElement<HTMLElement>("selected-fighter-title");
const selectedFighterRole = requireElement<HTMLElement>("selected-fighter-role");
const ticketPlayerName = requireElement<HTMLElement>("ticket-player-name");
const ticketRivalName = requireElement<HTMLElement>("ticket-rival-name");
const confirmFighterButton =
  requireElement<HTMLButtonElement>("confirm-fighter-button");
const confirmFighterLabel =
  requireElement<HTMLElement>("confirm-fighter-label");
const selectBackButton =
  requireElement<HTMLButtonElement>("select-back-button");
const hud = requireElement<HTMLElement>("hud");
const pauseScreen = requireElement<HTMLElement>("pause-screen");
const resultScreen = requireElement<HTMLElement>("result-screen");
const announcer = requireElement<HTMLElement>("announcer");
const matchTimer = requireElement<HTMLElement>("match-timer");
const playerHud = requireElement<HTMLElement>("player-hud");
const cpuHud = requireElement<HTMLElement>("cpu-hud");
const playerPortrait = requireElement<HTMLElement>("player-portrait");
const cpuPortrait = requireElement<HTMLElement>("cpu-portrait");
const playerName = requireElement<HTMLElement>("player-name");
const cpuName = requireElement<HTMLElement>("cpu-name");
const playerDamage = requireElement<HTMLElement>("player-damage");
const cpuDamage = requireElement<HTMLElement>("cpu-damage");
const playerShield = requireElement<HTMLElement>("player-shield");
const cpuShield = requireElement<HTMLElement>("cpu-shield");
const playerStocks = requireElement<HTMLElement>("player-stocks");
const cpuStocks = requireElement<HTMLElement>("cpu-stocks");
const playerController = requireElement<HTMLElement>("player-controller");
const cpuController = requireElement<HTMLElement>("cpu-controller");
const resultKicker = requireElement<HTMLElement>("result-kicker");
const resultTitle = requireElement<HTMLElement>("result-title");
const resultCopy = requireElement<HTMLElement>("result-copy");
const damageFlash = requireElement<HTMLElement>("damage-flash");
const startButton = requireElement<HTMLButtonElement>("start-button");
const resumeButton = requireElement<HTMLButtonElement>("resume-button");
const mainMenuButton = requireElement<HTMLButtonElement>("main-menu-button");
const rematchButton = requireElement<HTMLButtonElement>("rematch-button");
const changeFighterButton =
  requireElement<HTMLButtonElement>("change-fighter-button");

const input = new KeyboardInput();
const audio = new GameAudio();
const simulation = new GameSimulation();
const renderer = new GameRenderer(canvas);

let previousTime = performance.now();
const fixedStepDriver = new FixedStepDriver(FIXED_STEP);
let resultTimeout = 0;
let announcementTimeout = 0;
let previousStockSignature = "";
let selectedFighter: FighterId = "dog";
let reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const debugMetricsEnabled = new URLSearchParams(window.location.search).has("debug");
const debugMode = new URLSearchParams(window.location.search).get("debug");
const COMBAT_LAB_SECONDS = 10 * 60;
const combatLab =
  debugMode === "combat" ? new CombatLab(simulation, gameShell) : null;
const frameSamples: number[] = [];
let frameSampleCounter = 0;
const rosterButtons = new Map<string, HTMLButtonElement>();

function createRosterCard(): void {
  for (const fighter of ROSTER) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "roster-card";
    button.dataset.fighter = fighter.id;
    button.style.setProperty("--card-accent", fighter.accent);
    button.setAttribute("aria-label", `${fighter.name}, ${fighter.title}`);

    const number = document.createElement("span");
    number.className = "roster-card-number";
    number.textContent = fighter.number;

    const visual = document.createElement("span");
    visual.className = "roster-card-visual";
    if (fighter.portrait) {
      const image = document.createElement("img");
      image.src = fighter.portrait;
      image.alt = "";
      image.draggable = false;
      visual.append(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "roster-card-placeholder";
      placeholder.setAttribute("aria-hidden", "true");
      placeholder.append(document.createElement("i"), document.createElement("b"));
      visual.append(placeholder);
    }

    const copy = document.createElement("span");
    copy.className = "roster-card-copy";
    const name = document.createElement("strong");
    name.textContent = fighter.name;
    const title = document.createElement("small");
    title.textContent = fighter.title;
    copy.append(name, title);

    const status = document.createElement("span");
    status.className = "roster-card-status";
    const placeholder = PLACEHOLDER_TEMPLATES[fighter.id].toUpperCase();
    status.textContent = fighter.available
      ? `TEMP / ${placeholder} KIT`
      : "MOVESET PENDING";

    button.append(number, visual, copy, status);
    if (isPlayableFighter(fighter)) {
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", () => setSelectedFighter(fighter.id));
    } else {
      button.disabled = true;
      button.classList.add("is-locked");
    }
    rosterButtons.set(fighter.id, button);
    rosterGrid.append(button);
  }
}

function setSelectedFighter(fighterId: FighterId): void {
  const fighter = getRosterFighter(fighterId);
  const rivalId = getDefaultRival(fighterId);
  const rival = getRosterFighter(rivalId);
  selectedFighter = fighterId;

  characterSelect.style.setProperty("--fighter-accent", fighter.accent);
  selectShowcase.dataset.fighter = fighter.id;
  selectedNameGhost.textContent = fighter.name;
  selectedFighterImage.src = fighter.portrait ?? "";
  selectedFighterImage.alt = `${fighter.name} selected`;
  selectedFighterNumber.textContent = `CHALLENGER ${fighter.number}`;
  selectedFighterName.textContent = fighter.name;
  selectedFighterTitle.textContent = fighter.title;
  selectedFighterRole.textContent = fighter.role;
  ticketPlayerName.textContent = fighter.name;
  ticketRivalName.textContent = rival.name;
  confirmFighterLabel.textContent = `LOCK IN ${fighter.name}`;

  for (const [id, button] of rosterButtons) {
    const isSelected = id === fighterId;
    button.classList.toggle("is-selected", isSelected);
    if (!button.disabled) {
      button.setAttribute("aria-pressed", String(isSelected));
    }
  }
}

function cycleSelectedFighter(direction: -1 | 1): void {
  const currentIndex = AVAILABLE_FIGHTERS.findIndex(
    (fighter) => fighter.id === selectedFighter,
  );
  const nextIndex =
    (currentIndex + direction + AVAILABLE_FIGHTERS.length) %
    AVAILABLE_FIGHTERS.length;
  const next = AVAILABLE_FIGHTERS[nextIndex];
  if (next) {
    setSelectedFighter(next.id);
    rosterButtons.get(next.id)?.focus({ preventScroll: true });
  }
}

function showTitle(): void {
  simulation.status = "title";
  characterSelect.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");
  hud.classList.add("is-hidden");
  titleScreen.classList.remove("is-hidden");
  gameShell.dataset.screen = "title";
  startButton.focus({ preventScroll: true });
}

function showCharacterSelect(): void {
  audio.unlock();
  simulation.status = "title";
  titleScreen.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");
  hud.classList.add("is-hidden");
  characterSelect.classList.remove("is-hidden");
  setSelectedFighter(selectedFighter);
  gameShell.dataset.screen = "character-select";
  rosterButtons.get(selectedFighter)?.focus({ preventScroll: true });
}

function updateControllerLabels(): void {
  const player = getRosterFighter(simulation.playerId);
  const cpu = getRosterFighter(simulation.cpuId);
  playerController.textContent = `PLAYER 1 / ${player.title}`;
  cpuController.textContent = `CPU / ${cpu.title}`;
  playerName.textContent = player.name;
  cpuName.textContent = cpu.name;
  playerPortrait.style.backgroundImage = `url("${player.portrait ?? ""}")`;
  cpuPortrait.style.backgroundImage = `url("${cpu.portrait ?? ""}")`;
  playerHud.style.setProperty("--accent", player.accent);
  cpuHud.style.setProperty("--accent", cpu.accent);
  playerStocks.setAttribute("aria-label", `${player.name} stocks`);
  cpuStocks.setAttribute("aria-label", `${cpu.name} stocks`);
  gameShell.dataset.playerFighter = simulation.playerId;
  gameShell.dataset.cpuFighter = simulation.cpuId;
}

async function preloadRosterPortraits(
  onProgress: (progress: number) => void,
): Promise<void> {
  const portraits = ROSTER.flatMap((fighter) =>
    fighter.portrait ? [fighter.portrait] : [],
  );
  let loaded = 0;
  await Promise.all(
    portraits.map(
      (source) =>
        new Promise<void>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            loaded += 1;
            onProgress(loaded / portraits.length);
            resolve();
          };
          image.onerror = () => {
            reject(new Error(`Roster portrait failed to load: ${source}`));
          };
          image.src = source;
        }),
    ),
  );
}

function recordFrameCadence(rawFrameMilliseconds: number): void {
  if (!debugMetricsEnabled || rawFrameMilliseconds <= 0) {
    return;
  }
  frameSamples.push(rawFrameMilliseconds);
  if (frameSamples.length > 240) {
    frameSamples.shift();
  }
  frameSampleCounter += 1;
  if (frameSamples.length < 120 || frameSampleCounter % 30 !== 0) {
    return;
  }
  const sorted = [...frameSamples].sort((left, right) => left - right);
  const average =
    frameSamples.reduce((sum, value) => sum + value, 0) / frameSamples.length;
  const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? average;
  const maximum = sorted.at(-1) ?? average;
  gameShell.dataset.debugFps = (1000 / average).toFixed(1);
  gameShell.dataset.debugAverageMs = average.toFixed(2);
  gameShell.dataset.debugP95Ms = p95.toFixed(2);
  gameShell.dataset.debugMaxMs = maximum.toFixed(2);
  gameShell.dataset.debugSamples = frameSamples.length.toString();
}

function startMatch(): void {
  audio.unlock();
  window.clearTimeout(resultTimeout);
  const matchPlayer =
    debugMode === "dog-victory" ||
    debugMode === "grab-lab" ||
    debugMode === "grab-hold" ||
    debugMode === "combat"
      ? "dog"
      : selectedFighter;
  const matchCpu =
    matchPlayer === "dog" &&
    (debugMode === "dog-victory" ||
      debugMode === "grab-lab" ||
      debugMode === "grab-hold" ||
      debugMode === "combat")
      ? "tiger"
      : getDefaultRival(matchPlayer);
  simulation.setMatchup(matchPlayer, matchCpu);
  simulation.reset();
  previousStockSignature = "";
  fixedStepDriver.reset();
  titleScreen.classList.add("is-hidden");
  characterSelect.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");
  hud.classList.remove("is-hidden");
  updateControllerLabels();
  gameShell.dataset.screen = "match";
  if (debugMode === "dog-victory") {
    simulation.status = "playing";
    simulation.countdown = 0;
    const tiger = simulation.getFighter("tiger");
    tiger.stocks = 1;
    tiger.position.y = WORLD_HEIGHT + 200;
  }
  if (debugMode === "grab-lab" || debugMode === "grab-hold") {
    simulation.status = "playing";
    simulation.countdown = 0;
    simulation.cpuEnabled = false;
    const dog = simulation.getFighter("dog");
    const tiger = simulation.getFighter("tiger");
    dog.position.x = 880;
    dog.previousPosition.copy(dog.position);
    dog.facing = 1;
    tiger.position.x = 975;
    tiger.previousPosition.copy(tiger.position);
    tiger.facing = -1;
  }
  if (debugMode === "combat") {
    simulation.status = "playing";
    simulation.countdown = 0;
    simulation.timeRemaining = COMBAT_LAB_SECONDS;
    simulation.cpuEnabled = true;
    simulation.resetTrainingPositions();
    combatLab?.resetForMatch();
  }
  if (debugMode === "grab-hold") {
    const idleDebugInput = {
      moveX: 0 as const,
      jumpPressed: false,
      attackPressed: false,
      specialPressed: false,
      grabPressed: false,
      shieldPressed: false,
      shieldHeld: false,
      dashHeld: false,
      downHeld: false,
    };
    simulation.step(FIXED_STEP, {
      ...idleDebugInput,
      grabPressed: true,
    });
    for (let frame = 0; frame < 12; frame += 1) {
      simulation.step(FIXED_STEP, idleDebugInput);
    }
    simulation.pause();
  }
  updateHud();
}

function togglePause(): void {
  if (simulation.status === "paused") {
    simulation.resume();
    pauseScreen.classList.add("is-hidden");
    fixedStepDriver.reset();
    previousTime = performance.now();
    return;
  }
  if (simulation.status === "playing" || simulation.status === "countdown") {
    simulation.pause();
    pauseScreen.classList.remove("is-hidden");
    resumeButton.focus({ preventScroll: true });
  }
}

function returnToMainMenu(): void {
  window.clearTimeout(resultTimeout);
  combatLab?.stopRecording();
  showTitle();
  fixedStepDriver.reset();
  previousTime = performance.now();
}

function announce(text: string): void {
  window.clearTimeout(announcementTimeout);
  announcer.textContent = text;
  announcer.classList.remove("is-active");
  void announcer.offsetWidth;
  announcer.classList.add("is-active");
  announcementTimeout = window.setTimeout(() => {
    announcer.classList.remove("is-active");
  }, 820);
}

function showResult(winner: FighterId): void {
  const playerWon = winner === simulation.playerId;
  const fighter = getRosterFighter(winner);
  resultKicker.textContent = playerWon ? "VICTORY" : "DEFEAT";
  resultTitle.textContent = fighter.name;
  resultCopy.textContent = `${fighter.title} CLAIMS CLOUDBREAK`;
  resultScreen.classList.remove("is-hidden");
  gameShell.dataset.screen = "result";
}

function handleEvent(event: GameEvent): void {
  renderer.handleEvent(event);
  audio.handleEvent(event);
  if (event.type === "announce") {
    announce(event.text);
  }
  if (event.type === "hit" || event.type === "ko" || event.type === "shield-break") {
    damageFlash.classList.remove("is-active");
    void damageFlash.offsetWidth;
    damageFlash.classList.add("is-active");
  }
  if (event.type === "gameover") {
    resultTimeout = window.setTimeout(() => showResult(event.winner), 820);
  }
}

function drainSimulationEvents(): void {
  for (const event of simulation.drainEvents()) {
    handleEvent(event);
  }
}

function updateStocks(container: HTMLElement, amount: number): void {
  container.replaceChildren();
  for (let stock = 0; stock < amount; stock += 1) {
    container.append(document.createElement("i"));
  }
}

function damageColor(damage: number): string {
  if (damage < 55) {
    return "#ffffff";
  }
  if (damage < 105) {
    return "#ffd27a";
  }
  if (damage < 160) {
    return "#ff8f55";
  }
  return "#ff5564";
}

function updateHud(): void {
  const player = simulation.getFighter(simulation.playerId);
  const cpu = simulation.getFighter(simulation.cpuId);
  const totalSeconds = Math.ceil(simulation.timeRemaining);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  matchTimer.textContent = `${minutes}:${seconds}`;
  playerDamage.textContent = Math.floor(player.damage).toString();
  cpuDamage.textContent = Math.floor(cpu.damage).toString();
  playerDamage.style.color = damageColor(player.damage);
  cpuDamage.style.color = damageColor(cpu.damage);
  playerShield.style.transform = `scaleX(${player.shield / 100})`;
  cpuShield.style.transform = `scaleX(${cpu.shield / 100})`;

  if (debugMetricsEnabled) {
    gameShell.dataset.debugPlayerState = player.state;
    gameShell.dataset.debugCpuState = cpu.state;
    gameShell.dataset.debugPlayerHolding = player.heldFighter ?? "";
    gameShell.dataset.debugCpuHolding = cpu.heldFighter ?? "";
    gameShell.dataset.debugPlayerGrabbedBy = player.grabbedBy ?? "";
    gameShell.dataset.debugCpuGrabbedBy = cpu.grabbedBy ?? "";
    const dog = simulation.fighters.find((fighter) => fighter.id === "dog");
    const tiger = simulation.fighters.find((fighter) => fighter.id === "tiger");
    gameShell.dataset.debugDogState = dog?.state ?? "";
    gameShell.dataset.debugTigerState = tiger?.state ?? "";
    gameShell.dataset.debugDogHolding = dog?.heldFighter ?? "";
    gameShell.dataset.debugTigerHolding = tiger?.heldFighter ?? "";
    gameShell.dataset.debugDogGrabbedBy = dog?.grabbedBy ?? "";
    gameShell.dataset.debugTigerGrabbedBy = tiger?.grabbedBy ?? "";
    gameShell.dataset.debugDogCombo = String(dog?.comboCount ?? 0);
    gameShell.dataset.debugTigerCombo = String(tiger?.comboCount ?? 0);
    gameShell.dataset.debugDogLedge = dog?.ledgeId ?? "";
    gameShell.dataset.debugTigerLedge = tiger?.ledgeId ?? "";
    gameShell.dataset.debugDogJumps = String(dog?.jumpsRemaining ?? 0);
    gameShell.dataset.debugTigerJumps = String(tiger?.jumpsRemaining ?? 0);
    gameShell.dataset.debugDogX = dog?.position.x.toFixed(1) ?? "";
    gameShell.dataset.debugTigerX = tiger?.position.x.toFixed(1) ?? "";
    gameShell.dataset.debugDogVelocityX = dog?.velocity.x.toFixed(1) ?? "";
    gameShell.dataset.debugTigerVelocityX =
      tiger?.velocity.x.toFixed(1) ?? "";
  }

  const stockSignature = `${player.id}:${player.stocks}:${cpu.id}:${cpu.stocks}`;
  if (stockSignature !== previousStockSignature) {
    previousStockSignature = stockSignature;
    updateStocks(playerStocks, Math.max(0, player.stocks));
    updateStocks(cpuStocks, Math.max(0, cpu.stocks));
  }
}

function frame(now: number): void {
  const rawFrameDelta = Math.max(0, (now - previousTime) / 1000);
  previousTime = now;
  recordFrameCadence(rawFrameDelta * 1000);

  if (simulation.status !== "paused" || combatLab?.isReplaying()) {
    fixedStepDriver.advance(rawFrameDelta, () => {
      const frameInput = input.read();
      if (combatLab) {
        const wasReplaying = combatLab.isReplaying();
        combatLab.stepSimulation(frameInput);
        if (
          wasReplaying &&
          !combatLab.isReplaying() &&
          simulation.status === "paused"
        ) {
          pauseScreen.classList.remove("is-hidden");
        }
      } else {
        simulation.step(FIXED_STEP, frameInput);
      }
      drainSimulationEvents();
    });
  }

  renderer.render(
    simulation,
    fixedStepDriver.interpolationAlpha(),
    simulation.status === "paused" || reducedMotion ? 0 : rawFrameDelta,
  );
  updateHud();
  combatLab?.render();
  requestAnimationFrame(frame);
}

createRosterCard();
setSelectedFighter("dog");

startButton.addEventListener("click", showCharacterSelect);
confirmFighterButton.addEventListener("click", startMatch);
selectBackButton.addEventListener("click", showTitle);
rematchButton.addEventListener("click", startMatch);
changeFighterButton.addEventListener("click", showCharacterSelect);
resumeButton.addEventListener("click", togglePause);
mainMenuButton.addEventListener("click", returnToMainMenu);
window.addEventListener("keydown", (event) => {
  const selectIsOpen = !characterSelect.classList.contains("is-hidden");
  const combatMatchIsOpen =
    debugMode === "combat" && gameShell.dataset.screen === "match";
  const grabHoldStepIsOpen =
    debugMode === "grab-hold" &&
    gameShell.dataset.screen === "match" &&
    simulation.status === "paused";

  if (
    grabHoldStepIsOpen &&
    (event.code === "KeyA" || event.code === "KeyD")
  ) {
    event.preventDefault();
    simulation.stepPausedFrame(input.read());
    drainSimulationEvents();
    updateHud();
    return;
  }

  if (combatMatchIsOpen && event.code === "Backquote") {
    event.preventDefault();
    combatLab?.toggleRecording();
    return;
  }
  if (combatMatchIsOpen && event.code === "Digit0") {
    event.preventDefault();
    if (combatLab?.replayLatest()) {
      pauseScreen.classList.add("is-hidden");
      fixedStepDriver.reset();
      previousTime = performance.now();
    }
    return;
  }
  if (
    combatMatchIsOpen &&
    event.code === "Period" &&
    simulation.status === "paused"
  ) {
    event.preventDefault();
    combatLab?.stepSimulation(input.read(), true);
    drainSimulationEvents();
    updateHud();
    combatLab?.render();
    return;
  }
  if (combatMatchIsOpen && event.code === "BracketLeft") {
    event.preventDefault();
    combatLab?.stopRecording();
    simulation.resetTrainingPositions();
    combatLab?.startRecording("training-reset");
    updateHud();
    combatLab?.render();
    return;
  }
  if (combatMatchIsOpen && event.code === "KeyC") {
    event.preventDefault();
    combatLab?.toggleCpu();
    updateHud();
    combatLab?.render();
    return;
  }
  if (combatMatchIsOpen && event.code === "KeyL") {
    event.preventDefault();
    combatLab?.stopRecording();
    simulation.resetTrainingPositions();
    simulation.stageLedgeScenario(simulation.playerId, "main-right");
    combatLab?.startRecording("ledge-scenario");
    updateHud();
    combatLab?.render();
    return;
  }
  if (
    combatMatchIsOpen &&
    (event.code === "Minus" || event.code === "Equal")
  ) {
    event.preventDefault();
    combatLab?.stopRecording();
    const target = simulation.getFighter(simulation.cpuId);
    const change = event.code === "Minus" ? -10 : 10;
    target.damage = Math.max(0, Math.min(999, target.damage + change));
    simulation.clearComboScaling(target.id);
    combatLab?.startRecording("damage-set");
    updateHud();
    combatLab?.render();
    return;
  }

  if (event.code === "Escape") {
    event.preventDefault();
    if (selectIsOpen) {
      showTitle();
      return;
    }
    togglePause();
  }
  if (event.code === "Enter" && simulation.status === "title") {
    event.preventDefault();
    if (selectIsOpen) {
      startMatch();
    } else {
      showCharacterSelect();
    }
  }
  if (
    selectIsOpen &&
    (event.code === "ArrowLeft" || event.code === "KeyA")
  ) {
    event.preventDefault();
    cycleSelectedFighter(-1);
  }
  if (
    selectIsOpen &&
    (event.code === "ArrowRight" || event.code === "KeyD")
  ) {
    event.preventDefault();
    cycleSelectedFighter(1);
  }
});
document.addEventListener("visibilitychange", () => {
  if (
    document.hidden &&
    (simulation.status === "playing" || simulation.status === "countdown")
  ) {
    simulation.pause();
    pauseScreen.classList.remove("is-hidden");
  }
  fixedStepDriver.reset();
  previousTime = performance.now();
});
window
  .matchMedia("(prefers-reduced-motion: reduce)")
  .addEventListener("change", (event) => {
    reducedMotion = event.matches;
  });

async function boot(): Promise<void> {
  try {
    let rendererProgress = 0;
    let rosterProgress = 0;
    const updateLoadingProgress = () => {
      const progress = rendererProgress * 0.72 + rosterProgress * 0.28;
      loadingProgress.style.width = `${Math.round(progress * 100)}%`;
    };
    await Promise.all([
      renderer.load((progress) => {
        rendererProgress = progress;
        updateLoadingProgress();
      }),
      preloadRosterPortraits((progress) => {
        rosterProgress = progress;
        updateLoadingProgress();
      }),
    ]);
    loadingScreen.classList.add("is-hidden");
    if (debugMode === "combat") {
      startMatch();
    } else {
      titleScreen.classList.remove("is-hidden");
      gameShell.dataset.screen = "title";
    }
    fixedStepDriver.reset();
    previousTime = performance.now();
    requestAnimationFrame(frame);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown loading error";
    loadingScreen.classList.add("has-error");
    loadingScreen.innerHTML = `<p>THE CANOPY COULD NOT LOAD</p><small>${message}</small>`;
    console.error(error);
  }
}

void boot();
